import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { flow } from 'fp-ts/function';
import { each, isNil, set, unset } from 'lodash/fp';

import { TextKey } from '@mews-distributor/common';
import { ROUTE_SET } from '../../../../modules/app/actionTypes';
import { BOOKING_RESTARTED } from '../../../../modules/global/actionTypes';
import { FETCH_PAYMENT_CONFIGURATION_STARTED } from '../../../../modules/paymentConfigurations/actionTypes';
import { CREATE_RESERVATION_GROUP_STARTED } from '../../../../modules/reservationGroups/actionTypes';
import {
    CHECKOUT_PREPARATION_FAILED,
    CHECKOUT_PREPARATION_SKIPPED,
    CHECKOUT_PREPARATION_STARTED,
    CHECKOUT_PREPARATION_SUCCEEDED,
    CHECKOUT_REQUEST_STARTED,
} from './actionTypes';

const setStatusTextKey = (textKey: $TSFixMe) => set('statusTextKey', textKey);

const clearFieldData = (key: $TSFixMe) =>
    flow(unset(['fields', key]), unset(['customerErrors', key]), unset(['customerValues', key]));

export const initialState = {
    hasFailed: false,
    hasPreparationFailed: false,
    isCheckoutInProgress: false,
    isFetching: false,
    isPreparing: false,
    statusTextKey: null,
    currentPaymentCardType: null,
    customerValues: {},
    customerErrors: {},
    fields: {},
    allFieldsTouched: false,
};

type PayloadWithError = PayloadAction<{ error: Error }>;

const checkoutSlice = createSlice({
    name: 'checkout',
    initialState,
    reducers: {
        setCustomerValue: (state, { payload: { key, value } }) => set(['customerValues', key], value)(state),

        validateCustomerValues: (state, { payload: errors }) => {
            let tempState = { ...state };
            each((e) => {
                tempState = isNil((e as $TSFixMe).error)
                    ? unset(['customerErrors', (e as $TSFixMe).key])(tempState)
                    : set(['customerErrors', (e as $TSFixMe).key], (e as $TSFixMe).error)(tempState);
            })(errors);

            return tempState;
        },

        touchField: (state, { payload: name }) => set(['fields', name], { touched: true }, state),

        touchAllFields: (state) => ({ ...state, allFieldsTouched: true }),

        changePaymentCardType: (state, { payload: type }) => ({ ...state, currentPaymentCardType: type }),
        clearPaymentCardValues: flow(
            clearFieldData('cvv'),
            clearFieldData('holderName'),
            clearFieldData('expiration'),
            clearFieldData('number')
        ),

        clearCustomerErrors: (state) => ({ ...state, customerErrors: {} }),

        confirmReservation: (state) => ({ ...state, isFetching: false, isCheckoutInProgress: false }),

        failCheckoutRequest: (state, _payload: PayloadWithError) => ({ ...state, isFetching: false, hasFailed: true }),

        startCardEncryption: setStatusTextKey(TextKey.VerifyPaymentCardProgress),

        cardEncryptionSuccess: (state) => ({ ...state }),

        failCardEncryption: (state, _payload: PayloadWithError) => ({ ...state }),
    },
    extraReducers: (builder) => {
        builder
            .addCase(ROUTE_SET, (state) => ({ ...state, hasFailed: false, isCheckoutInProgress: false }))
            .addCase(CHECKOUT_REQUEST_STARTED, (state) => ({
                ...state,
                isFetching: true,
                hasFailed: false,
                isCheckoutInProgress: true,
            }))
            .addCase(CHECKOUT_PREPARATION_STARTED, (state) => ({
                ...state,
                isPreparing: true,
                hasPreparationFailed: false,
            }))
            .addCase(CHECKOUT_PREPARATION_SUCCEEDED, (state) => ({ ...state, isPreparing: false }))
            .addCase(CHECKOUT_PREPARATION_FAILED, (state) => ({
                ...state,
                isPreparing: false,
                hasPreparationFailed: true,
            }))
            .addCase(CHECKOUT_PREPARATION_SKIPPED, (state) => ({
                ...state,
                isFetching: false,
                hasFailed: false,
                isPreparing: false,
                hasPreparationFailed: false,
            }))
            .addCase(CREATE_RESERVATION_GROUP_STARTED, setStatusTextKey(TextKey.CreatingReservation))
            .addCase(FETCH_PAYMENT_CONFIGURATION_STARTED, setStatusTextKey(TextKey.PreparingSecureCheckout))

            .addCase(BOOKING_RESTARTED, unset(['customerValues', 'agreeWithConditions']));
    },
});

const {
    setCustomerValue,
    validateCustomerValues,
    touchField,
    touchAllFields,
    changePaymentCardType,
    clearPaymentCardValues,
    clearCustomerErrors,
    confirmReservation,
    failCheckoutRequest,
    startCardEncryption,
    cardEncryptionSuccess,
    failCardEncryption,
} = checkoutSlice.actions;

const clearPaymentCardType = () => changePaymentCardType(null);

export {
    setCustomerValue,
    validateCustomerValues,
    touchField,
    touchAllFields,
    changePaymentCardType,
    clearPaymentCardType,
    clearPaymentCardValues,
    clearCustomerErrors,
    confirmReservation,
    failCheckoutRequest,
    startCardEncryption,
    cardEncryptionSuccess,
    failCardEncryption,
};

export const { reducer: checkoutReducer } = checkoutSlice;
